/**
 * 板発注画面の板情報パネルのエリアグループ絞り込み (AC-BIN-103)。
 *
 * 板のデータはエリアグループ単位で届くので、 絞り込みも 9 エリアではなく 5 グループで行う。
 * 売り板・買い板を並べるため売買区分では絞らない。 絞り込みは板発注タブの中だけで持ち、
 * 全体フィルター (AC-BIN-102) には反映しない (design `bidding/intraday-price.html` の
 * `boardGroupSel` / `visibleGroups` / `boardOrders`)。
 */
import type { BiddingMarketBid } from '@ebalance/proto/bffuser/v1/bidding_intraday_market_model_pb'

import { defineFilterCodec, listOf, oneOf } from '~/models/filterCodec'
import {
  MARKET_AREA_GROUPS,
  type MarketAreaGroup,
} from '~/pages/IntradayBidding/models/marketBoardPivot'

/**
 * 板発注タブの絞り込みの保存形 (AC-SC-031)。 `areaGroups` は選んだグループで、
 * 空 = 「すべて」 (design: 空 = すべて)。 全 5 件を個別に選んだ状態は空と区別して保持する
 * (UI 辞書 multi-select-summary.md「全件を個別に選んだ状態は『すべて』と同じ表示にしない」)。
 */
export type BoardAreaGroupFilters = {
  areaGroups: MarketAreaGroup[]
}

export const boardAreaGroupFiltersCodec =
  defineFilterCodec<BoardAreaGroupFilters>({
    defaults: () => ({ areaGroups: [] }),
    fields: { areaGroups: listOf(oneOf<MarketAreaGroup>(MARKET_AREA_GROUPS)) },
  })

/** 板に出すエリアグループ。 選択が空なら全 5 グループ。 並びは選んだ順ではなく板の列の順。 */
export function visibleAreaGroups(
  selected: readonly MarketAreaGroup[],
): readonly MarketAreaGroup[] {
  if (selected.length === 0) return MARKET_AREA_GROUPS
  return MARKET_AREA_GROUPS.filter((group) => selected.includes(group))
}

/**
 * 選んだグループの注文だけを残す。 板の価格帯・合計 MW・最良気配・スプレッドを
 * 選んだグループだけで出し直すため、 pivot の前に絞る (design `boardOrders`)。
 */
export function filterBidsByAreaGroups(
  bids: readonly BiddingMarketBid[],
  groups: readonly MarketAreaGroup[],
): BiddingMarketBid[] {
  if (groups.length === MARKET_AREA_GROUPS.length) return [...bids]
  return bids.filter((bid) => groups.includes(bid.areaGroup as MarketAreaGroup))
}
